import React from 'react';
import { PdfContent, PdfItem } from '../../type';
interface IPdfPreview {
  content: PdfContent;
}

const formatCurrency = (amount: string | number, currency = 'JPY') => {
  if (amount === undefined || amount === null) {
    return '';
  }
  if (typeof amount === 'string') {
    return amount;
  }
  if (currency === 'JPY') {
    return `¥${amount.toFixed(0)}`;
  }
  return `$${amount.toFixed(2)}`;
};

const isValidImage = (url: string) => {
  return (
    url &&
    typeof url === 'string' &&
    (url.startsWith('http://') || url.startsWith('https://'))
  );
};

const calculateSubtotal = (orderLines: PdfItem[]) => {
  return orderLines.reduce((total, item) => {
    if (
      typeof item.itemPrice !== 'number' ||
      typeof item.itemQuantity !== 'number'
    ) {
      return 0;
    }
    return total + item.itemPrice * item.itemQuantity;
  }, 0);
};

const calculateTotal = (
  data: Pick<PdfContent, 'tax' | 'shippingFee' | 'orderLines'>
) => {
  const { tax, shippingFee, orderLines } = data;
  if (
    typeof tax?.value !== 'number' ||
    typeof shippingFee?.value !== 'number'
  ) {
    return 0;
  }

  let total = calculateSubtotal(orderLines);
  if (tax?.enabled) {
    if (tax.type === 'percentage') {
      total += total * (tax.value / 100);
    } else {
      total += tax.value;
    }
  }

  if (shippingFee?.enabled) {
    if (shippingFee.type === 'percentage') {
      total += total * (shippingFee.value / 100);
    } else {
      total += shippingFee.value;
    }
  }

  return total;
};

const LogoPlaceholder = () => (
  <div
    style={{
      width: '100px',
      height: '100px',
      backgroundColor: '#f5f5f5',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#999',
      fontSize: '14px',
      marginBottom: '20px',
    }}
  >
    Logo
  </div>
);

const PdfPreview: React.FC<IPdfPreview> = ({ content }) => {
  const {
    orderLines,
    orderHeaders,
    currency,
    tax,
    shippingFee,
    senderInformation,
    receiverInformation,
    invoiceInformation,
    customFields1,
    customFields2,
    customFields3,
    customFields4,
  } = content;

  const reactElement = () => (
    <div
      id="invoice-preview"
      style={{
        maxWidth: '17cm',
        margin: '0 auto',
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
        border: '1px solid #ddd',
        borderRadius: '4px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '30px',
        }}
      >
        {/* Left Side */}
        <div style={{ flex: 1 }}>
          {invoiceInformation?.logo && isValidImage(invoiceInformation.logo) ? (
            <img
              src={invoiceInformation.logo}
              alt="Company Logo"
              style={{
                maxHeight: '100px',
                marginBottom: '20px',
                borderRadius: '50%',
                objectFit: 'cover',
                width: '100px',
                height: '100px',
              }}
            />
          ) : (
            <LogoPlaceholder />
          )}
          <div>
            <h3 style={{ margin: '0 0 10px 0' }}>{senderInformation?.title}</h3>
            <p style={{ margin: '0' }}>{senderInformation?.name}</p>
            <p style={{ margin: '0' }}>{senderInformation?.description}</p>
          </div>
          <div style={{ marginTop: '20px' }}>
            <h3 style={{ margin: '0 0 10px 0' }}>
              {receiverInformation?.title}
            </h3>
            <p style={{ margin: '0' }}>{receiverInformation?.name}</p>
            <p style={{ margin: '0' }}>{receiverInformation?.description}</p>
          </div>
        </div>

        {/* Right Side */}
        <div style={{ flex: 1, textAlign: 'right' }}>
          <h1 style={{ margin: '0 0 10px 0' }}>{invoiceInformation?.title}</h1>
          <p style={{ margin: '0 0 20px 0' }}>
            #{invoiceInformation?.invoiceID}
          </p>

          {/* Custom Information */}
          <div>
            {customFields1?.enabled && (
              <p style={{ margin: '0 0 5px 0' }}>
                {customFields1.title}: {customFields1.value}
              </p>
            )}
            {customFields2?.enabled && (
              <p style={{ margin: '0 0 5px 0' }}>
                {customFields2.title}: {customFields2.value}
              </p>
            )}
            {customFields3?.enabled && (
              <p style={{ margin: '0 0 5px 0' }}>
                {customFields3.title}: {customFields3.value}
              </p>
            )}
            {customFields4?.enabled && (
              <p style={{ margin: '0 0 5px 0' }}>
                {customFields4.title}: {customFields4.value}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Order Items */}
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          marginBottom: '20px',
        }}
      >
        <thead>
          <tr style={{ borderBottom: '2px solid #ddd' }}>
            <th style={{ textAlign: 'left', padding: '10px' }}>
              {orderHeaders?.itemName}
            </th>
            <th style={{ textAlign: 'right', padding: '10px' }}>
              {orderHeaders?.itemQuantity}
            </th>
            <th style={{ textAlign: 'right', padding: '10px' }}>
              {orderHeaders?.itemPrice}
            </th>
            <th style={{ textAlign: 'right', padding: '10px' }}>
              {orderHeaders?.total}
            </th>
          </tr>
        </thead>
        <tbody>
          {orderLines.map((item, index) => (
            <tr key={index} style={{ borderBottom: '1px solid #ddd' }}>
              <td style={{ padding: '10px' }}>
                <div>{item.itemName}</div>
                {item.itemDescription && (
                  <div style={{ fontSize: '0.9em', color: '#666' }}>
                    {item.itemDescription}
                  </div>
                )}
              </td>
              <td style={{ textAlign: 'right', padding: '10px' }}>
                {item.itemQuantity}
              </td>
              <td style={{ textAlign: 'right', padding: '10px' }}>
                {formatCurrency(item.itemPrice, currency)}
              </td>
              <td style={{ textAlign: 'right', padding: '10px' }}>
                {typeof item.itemPrice === 'number' &&
                typeof item.itemQuantity === 'number'
                  ? formatCurrency(item.itemPrice * item.itemQuantity, currency)
                  : item.itemPrice}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Summary */}
      <div
        style={{
          marginLeft: 'auto',
          maxWidth: '300px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '10px',
          }}
        >
          <span>Subtotal:</span>
          <span>{formatCurrency(calculateSubtotal(orderLines), currency)}</span>
        </div>

        {tax?.enabled && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '10px',
            }}
          >
            <span>{tax.title}:</span>
            <span>
              {tax.type === 'percentage'
                ? `${tax.value}% (${formatCurrency(
                    typeof tax.value === 'string'
                      ? tax.value
                      : calculateSubtotal(orderLines) * (tax.value / 100)
                  )})`
                : formatCurrency(tax.value)}
            </span>
          </div>
        )}

        {shippingFee?.enabled && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '10px',
            }}
          >
            <span>{shippingFee.title}:</span>
            <span>
              {shippingFee.type === 'percentage'
                ? `${shippingFee.value}% (${formatCurrency(
                    typeof shippingFee.value === 'string'
                      ? shippingFee.value
                      : calculateSubtotal(orderLines) *
                          (shippingFee.value / 100)
                  )})`
                : formatCurrency(shippingFee.value)}
            </span>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: '2px solid #ddd',
            paddingTop: '10px',
            fontWeight: 'bold',
          }}
        >
          <span>Total:</span>
          <span>
            {formatCurrency(
              calculateTotal({ tax, shippingFee, orderLines }),
              currency
            )}
          </span>
        </div>
      </div>

      {/* Invoice Notes */}
      {invoiceInformation?.notes && (
        <div
          style={{
            marginTop: '30px',
            paddingTop: '20px',
            borderTop: '1px solid #ddd',
          }}
        >
          <p style={{ margin: '0', whiteSpace: 'pre-line' }}>
            {invoiceInformation.notes}
          </p>
        </div>
      )}
    </div>
  );

  return reactElement();
};

export default PdfPreview;
